/* eslint-disable camelcase */
import React, { useState, useEffect, useContext, useRef } from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import {
  MuiThemeProvider,
  defaultTheme,
  PhoneField,
} from '@nextiva/next-ui-material';
import {
  Alert,
  Button,
  CircularProgress,
  Divider,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Avatar as UnstyledAvatar,
  styled,
} from '@nextiva/next-ui-components';
import { dataSelector } from '@nextiva/global-store';
import { useSelector } from 'react-redux';
import { isEqual, differenceWith } from 'lodash';
import { useMutation, useQueryClient } from 'react-query';
import {
  RootContext,
  actionTypes as rootActionTypes,
} from 'RootContext/rootContext';

import { FeatureContext } from 'components/AccessResolvers/featureContext';
import {
  UserContext,
  actionTypes,
} from 'containers/pages/ManageUsers/usersContext';
import {
  postResetPassword,
  updateUser,
  getUser,
  getUserLicenses,
} from 'api/userApi';
import { userTeamMapping, getTeamMapping } from 'services/TOS';
import { getCXWorkspacesAtUserLevel } from 'services/CXWorkspaces/CXWorkspacesService';
import { assignUserLicenses, unassignUserLicenses } from 'api/licenseService';
import { getVoiceUserPhoneNumbers } from 'api/voiceUserApi';
import {
  updateUserDeps,
  patchUserPrimaryTeam,
  getCrmDepartment,
  getCrmDefaultDepartment,
} from 'api/crmApi';
import Hint from 'components/Hint';
import {
  ADMIN_LOCATIONS,
  ROLE,
  ROLE_FOR_OTHER_LOCATIONS,
  EMAIL_ADDRESS,
  PRIMARY_TEAM,
  ADMINISTRATION_ACCESS,
} from 'constants/toolTips';
import { CRM_LICENSES } from 'constants/crmLicenses';
import {
  useSnackbar,
  useGetCompanyInfo,
  useGetInfo,
  useGetTimezoneOptions,
} from 'hooks';
import { validData, isAccountManager, isValidEmail } from 'utils/validators';
import { isValidPhoneNumber } from 'utils/countries-helpers';
import { isLicenseFeatureAssigned } from 'utils/checkForAssignedFeature';
import {
  getNextivaCXLicensingStatus,
  CX_PAID_USER,
} from 'utils/getNextivaCXLicensingStatus';
import { assignedLicensesFeatureList } from 'utils/license-helpers';
import { getLocationName } from 'utils/display-helpers';
import EmailErrorMessage from 'containers/pages/ManageUsers/EmailErrorMessage';

import { useRootUserList } from 'customHooks/userCustomHooks';
import { WorkspaceChipSelect } from 'components/WorkspaceChipSelect/WorkspaceChipSelect';
import {
  useUpdateCXUser,
  useCreateCXUser,
} from 'hooks/useUpdateCXUser/useUpdateCXUser';
import {
  UserDrawerContext,
  actionTypes as UserDrawerActionTypes,
} from '../UserDrawerContent/UserDrawerContext';

import { adminAccessOptions } from '../adminAccessOptions';
import classes from './m-edit-user.scss';
import {
  LicenseSection,
  TeamsSection,
  Section,
  HistorySection,
} from './Sections';

const Avatar = styled(UnstyledAvatar)`
  font-size: 2rem;
  margin: 18px 0px 64px 0px;
  line-height: 2.375rem;
`;

const StyledTooltip = styled(Tooltip)`
  border-radius: 4px;
`;

const ResetButtonStyle = {
  backgroundColor: 'white',
  color: '#005FEC',
  borderColor: '#005FEC',
  lineHeight: '17px',
  padding: '5px 92px',
};

// TODO: change Non to None and nonSpectified to noRoleSpecified
const nonSpecified = {
  value: 'Non Specified',
  key: 'Non Specified',
  label: 'Non Specified',
  description: 'Non Specified',
  isDefault: true,
};

const getInitUserRole = (roles) => {
  const isAdmin = roles.find((role) => role.role_type === 'SUPER_ADMIN');

  switch (true) {
    case !!isAdmin:
      return isAdmin.id;
    case roles.length > 0:
      return roles[0].id;
    default:
      return nonSpecified.value;
  }
};

const getCrmPrimaryIdentifier = ({ profiles }) => {
  return (
    profiles.length > 0 &&
    (profiles[0]?.idents?.crm?.primary_identifier ||
      profiles[0]?.crmIdent?.primaryIdentifier ||
      null)
  );
};

const isVoiceUser = ({ profiles }) => {
  return (
    (profiles[0].idents?.nextOS.primary_identifier.length ||
      profiles[0].nextOSIdent?.primaryIdentifier.length) > 0
  );
};

const TooltipWithEnable = ({ enableTooltip, text, children }) => {
  if (enableTooltip) {
    return (
      <StyledTooltip placement="top" title={text}>
        {children}
      </StyledTooltip>
    );
  }

  return children;
};

const LICENSES_LINK = 'apps/platform-admin/#/licensing/licenses';

const EditUser = ({ toggleDrawer, impersonation }) => {
  const globalStoreData = useSelector(dataSelector);
  const sso = globalStoreData?.sso;
  const {
    featureState: {
      authV3Enabled,
      locationBasedAdmin: locationBasedAdminFeature,
      globalNextivaCXFlowEnabled,
      cxPrimaryOwnerRemoval,
    },
    corpAcctNbr,
  } = useContext(FeatureContext);
  const queryClient = useQueryClient();
  const {
    dispatch,
    userState: {
      teamsOptions,
      rolesOptions,
      locationsOptions,
      voiceSetupModel: { scrollToBottom },
      baseUserList,
      editUser: { active: activeStatus },
      editUser: user,
      originalUserLevelCXWorkspaces,
      updatedUserLevelCXWorkspaces,
      newCXUserWorkspaces,
    },
  } = useContext(UserContext);
  const { data: currentUser } = useGetInfo();

  const updateCXUser = useUpdateCXUser();
  const createCXUser = useCreateCXUser();

  const companyInfo = useGetCompanyInfo();
  const {
    userDrawerState: {
      assignedLicenses,
      licensesToAdd,
      baseAssignedLicenses,
      assignedTeams,
      baseAssignedTeams,
    },
    dispatchUserDrawer,
  } = useContext(UserDrawerContext);
  const { licenses } = useSelector(dataSelector);
  const timezoneOptions = useGetTimezoneOptions();
  const {
    rootState: {
      drawerSnackbar: { isOpen },
    },
    rootDispatch,
  } = useContext(RootContext);
  const { updateRootUserList } = useRootUserList();

  const [unsavedEditUserChanges, setUnsavedEditUserChanges] = useState(false);
  const [saveRequestBody, setSaveRequestBody] = useState({
    add_ids: [],
    drop_ids: [],
  });
  const [initialPhoneVal, setInitialPhoneVal] = useState({
    code: '+1',
    number: '',
  });
  const [userInput, setUserInput] = useState({
    ...user,
    phone: initialPhoneVal,
  });
  const [sending, setSending] = useState(false);
  const [pristine, setPristine] = useState(false);
  const [validation, setValidation] = useState({
    dataReady: 'Form is not ready.',
  });
  const [disabled, setSubmitDisabled] = useState(true);
  const [adminAccess, setAdminAccess] = useState();
  const [adminLocations, setAdminLocations] = useState([]);
  const [role, setRole] = useState('');
  const [usersRoles, setUsersRoles] = useState([]);
  const [primaryTeam, setPrimaryTeam] = useState('');
  const [primaryTeamDisable, setPrimaryTeamDisable] = useState(true);
  const [location, setLocation] = useState(
    user.profiles.length > 0 ? user.profiles[0].locationId : null,
  );
  const [locationDisable, setLocationDisable] = useState(true);
  const [phoneNumberDisable, setPhoneNumberDisable] = useState(true);
  const [crmUserId, setCrmUserId] = useState(null);
  const [multipleProfiles, setMultipleProfiles] = useState(false);
  const editUserDom = useRef(null);
  const setSnackbar = useSnackbar(true);
  const updateUserTeamMapping = useMutation(userTeamMapping);
  const usersEmailsList = baseUserList.reduce((emails, { email }) => {
    if (email !== user.email) {
      emails.push(email);
    }
    return emails;
  }, []);

  const didUserInitiallyHaveCXLicense =
    isLicenseFeatureAssigned(baseAssignedLicenses, 'CXSocialStandard') ||
    isLicenseFeatureAssigned(baseAssignedLicenses, 'CXSocialGrowth');
  const isAdminAndPaidCXUser =
    getNextivaCXLicensingStatus(
      queryClient.getQueryData('userLicensesWithAttributes')?.data,
    ) === CX_PAID_USER;
  const doesCurrentUserHaveCXLicense =
    isLicenseFeatureAssigned(assignedLicenses, 'CXSocialStandard') ||
    isLicenseFeatureAssigned(assignedLicenses, 'CXSocialGrowth') ||
    isLicenseFeatureAssigned(licensesToAdd, 'CXSocialStandard') ||
    isLicenseFeatureAssigned(licensesToAdd, 'CXSocialGrowth');
  const isWorkspaceFlowEnabled =
    globalNextivaCXFlowEnabled &&
    isAdminAndPaidCXUser &&
    doesCurrentUserHaveCXLicense;

  const superAdminRole = rolesOptions.find(
    (roleOption) => roleOption.roleType === 'SUPER_ADMIN',
  );

  const rolesTool =
    adminAccess === 'certain_loc_admin' ? 'Role (for other locations)' : 'Role';

  const rolesTip =
    adminAccess === 'certain_loc_admin' ? ROLE_FOR_OTHER_LOCATIONS : ROLE;

  const filteredRolesOptions =
    adminAccess === 'certain_loc_admin'
      ? rolesOptions.filter(
          (roleOption) => roleOption.roleType !== 'SUPER_ADMIN',
        )
      : rolesOptions;

  useEffect(() => {
    if (scrollToBottom && editUserDom.current) {
      editUserDom.current.scrollIntoView(false);
    }

    return dispatch({
      type: actionTypes.SET_VOICE_SETUP_MODEL,
      voiceSetupModel: { scrollToBottom: false },
    });
  }, [scrollToBottom]);

  useEffect(() => {
    const {
      first_name,
      firstName,
      last_name,
      lastName,
      phone,
      email,
    } = userInput;

    setValidation({
      ...validation,
      first_name: validData(30, first_name ?? firstName),
      last_name: validData(30, last_name ?? lastName),
      phone: isValidPhoneNumber(phone) ? '' : 'Invalid phone number',
      email: isValidEmail(email)
        ? (usersEmailsList.includes(email) &&
            'User with this email already exists') ||
          ''
        : EmailErrorMessage,
    });
  }, [userInput]);

  useEffect(() => {
    if (
      updatedUserLevelCXWorkspaces.length !==
      originalUserLevelCXWorkspaces.length
    ) {
      setPristine(false);
      setSubmitDisabled(false);
    }
  }, [updatedUserLevelCXWorkspaces]);

  useEffect(() => {
    setPristine(
      baseAssignedLicenses === assignedLicenses && licensesToAdd.length === 0,
    );
  }, [baseAssignedLicenses, assignedLicenses, licensesToAdd]);

  useEffect(() => {
    setPristine(baseAssignedTeams === assignedTeams);
  }, [assignedTeams, baseAssignedTeams]);

  useEffect(() => {
    if (Object.values(validation).some((v) => v !== '')) {
      setSubmitDisabled(true);
    } else {
      setSubmitDisabled(false);
    }
  }, [validation]);

  useEffect(() => {
    if (sending) {
      setSubmitDisabled(true);
    } else {
      setSubmitDisabled(false);
    }
  }, [sending]);

  useEffect(() => {
    getUser(user.email, authV3Enabled, user.nextivaUuid ?? user.nextiva_uuid)
      .then((res) => {
        setMultipleProfiles(
          res.multiple_profiles || res?.multipleProfiles || false,
        );

        setUserInput({
          ...userInput,
          security_pin: res.user.security_pin || res?.user?.securityPin || '',
        });
        setInitialPhoneVal({
          ...initialPhoneVal,
          number: res.user?.phone_number || res.user?.phoneNumber,
        });

        if (res.user.profiles.length > 0) {
          setLocation(
            res.user?.profiles[0]?.location_id ||
              res.user?.profiles[0]?.locationId,
          );
        }

        const crmProfile = getCrmPrimaryIdentifier(res.user);
        setCrmUserId(crmProfile);
        const voiceUser = isVoiceUser(res.user);

        setLocationDisable(voiceUser);
        setPhoneNumberDisable(voiceUser);
        if (voiceUser) {
          getVoiceUserPhoneNumbers(res.user.profiles[0].id)
            .then((data) => {
              const {
                primaryPhoneNumber: { phoneNumber },
              } = data;

              setInitialPhoneVal({ ...initialPhoneVal, number: phoneNumber });
            })
            .catch(() => {
              // TODO: add sanck bar on phone number error
            });
        }

        if (authV3Enabled) {
          dispatchUserDrawer({
            type: UserDrawerActionTypes.SET_BASE_ASSIGNED_TEAMS,
            baseAssignedTeams: res.departments,
          });
          dispatchUserDrawer({
            type: UserDrawerActionTypes.SET_ASSIGNED_TEAMS,
            assignedTeams: res.departments,
          });
        } else {
          getTeamMapping(corpAcctNbr, user.nextiva_uuid).then((response) => {
            dispatchUserDrawer({
              type: UserDrawerActionTypes.SET_BASE_ASSIGNED_TEAMS,
              baseAssignedTeams: response.data.data,
            });
            dispatchUserDrawer({
              type: UserDrawerActionTypes.SET_ASSIGNED_TEAMS,
              assignedTeams: response.data.data,
            });
          });
        }

        const roles = res.roles || [];

        setUsersRoles(roles);
        setRole(getInitUserRole(roles));
        if (crmProfile && !currentUser?.is_ace_user) {
          return getCrmDefaultDepartment(crmProfile);
        }

        return false;
      })
      .then((res) => {
        if (res.included) {
          // Handle primary Team if found
          setPrimaryTeam(res.included[0].attributes.sso_role_id);
          setPrimaryTeamDisable(false);
        } else {
          if (user.team?.[0]) {
            setPrimaryTeam(user.team[0].id);
          } else if (crmUserId) {
            setSnackbar({
              message: `${user.first_name ?? user.firstName} ${
                user.last_name ?? user.lastName
              } is required to have a team assigned. Please add primary team below.`,
              severity: 'error',
            });
          }
          setPrimaryTeamDisable(false);
          setSubmitDisabled(false);
        }
        setValidation({ dataReady: '' });
        dispatchUserDrawer({
          type: UserDrawerActionTypes.SET_TEAMS_LOADING,
          isTeamsLoading: false,
        });
      })
      .catch((error) => {
        setValidation({ dataReady: '' });
        if (error.code !== '403 Forbidden') {
          setSnackbar({
            message: 'Unable to retrieve user details.',
            severity: 'error',
          });
        }
        dispatchUserDrawer({
          type: UserDrawerActionTypes.SET_TEAMS_LOADING,
          isTeamsLoading: false,
        });
      });

    getUserLicenses(
      user.profiles[0].id,
      authV3Enabled,
      corpAcctNbr,
      user.nextivaUuid ?? user.nextiva_uuid,
    )
      .then((res) => {
        dispatchUserDrawer({
          type: UserDrawerActionTypes.SET_BASE_ASSIGNED_LICENSES,
          baseAssignedLicenses: res,
        });
        dispatchUserDrawer({
          type: UserDrawerActionTypes.SET_ASSIGNED_LICENSES,
          assignedLicenses: res,
        });
        dispatchUserDrawer({
          type: UserDrawerActionTypes.SET_LICENSES_LOADING,
          isLicensesLoading: false,
        });
      })
      .catch(() => {
        setSnackbar({
          message: 'Unable to retrieve user licenses.',
          severity: 'error',
        });
        dispatchUserDrawer({
          type: UserDrawerActionTypes.SET_LICENSES_LOADING,
          isLicensesLoading: false,
        });
      });
  }, []);

  const handlePhoneField = (abbr, code, number) => {
    setUserInput({ ...userInput, phone: { abbr, code, number } });

    // Check that the initial phone value differs from the form input
    if (code !== initialPhoneVal.code || number !== initialPhoneVal.number)
      setPristine(false);
  };

  const resetUserPassword = () => {
    postResetPassword(
      authV3Enabled ? user.nextivaUuid : user.email,
      authV3Enabled,
    )
      .then(() => {
        setSnackbar({
          message: `A password reset has been sent to ${
            user.first_name ?? user.firstName
          } ${user.last_name ?? user.lastName}`,
          severity: 'success',
        });
      })
      .catch(() => {
        setSnackbar({
          message: 'A password reset was not sent! Please contact support.',
          severity: 'error',
        });
      });
  };

  const handleInputChange = (e, id) => {
    const { value } = e.target;
    setUnsavedEditUserChanges(true);

    // Set onSave request body
    switch (id) {
      case 'adminAccess':
        setAdminAccess(value);
        if (value === 'entire_acc_admin') {
          setRole(superAdminRole.value);
        } else if (
          value === 'certain_loc_admin' &&
          role === superAdminRole.value
        ) {
          setRole('');
        }
        break;
      case 'adminLocations':
        setAdminLocations(value);
        break;
      case 'role':
        setRole(value);
        break;
      case 'primaryTeam':
        setPrimaryTeam(value);
        break;
      case 'location':
        setLocation(value);
        break;
      case 'timeZone':
        setUserInput({
          ...userInput,
          [id]: value,
        });

        setSaveRequestBody({
          ...saveRequestBody,
          preferred_time_zone: value,
        });
        break;
      default:
        setUserInput({
          ...userInput,
          [id]: value,
        });

        setSaveRequestBody({
          ...saveRequestBody,
          [id]: value,
        });
    }

    setPristine(false);
  };

  const onSave = async (event, unsavedChangesFlow = false) => {
    if (!cxPrimaryOwnerRemoval) {
      // If you attempt to remove a CX license from a CX primary owner user, then show error
      let isPrimaryAccountOwner = false;
      if (
        didUserInitiallyHaveCXLicense &&
        !(
          isLicenseFeatureAssigned(assignedLicenses, 'CXSocialStandard') ||
          isLicenseFeatureAssigned(assignedLicenses, 'CXSocialGrowth')
        )
      ) {
        await getCXWorkspacesAtUserLevel(
          sso,
          corpAcctNbr,
          user.nextiva_uuid ?? user.nextivaUuid,
        )
          .then((response) => {
            isPrimaryAccountOwner = response?.data?.primaryAccountOwner;
          })
          .catch(() => {
            // eslint-disable-next-line no-console
            console.error('Could not find CX user');
          });
      }

      if (isPrimaryAccountOwner) {
        setSnackbar({
          message:
            'This contact is a primary administrator. A primary administrator must be assigned for the account to use this license. Please assign another primary administrator for assistance, please call support.',
          severity: 'error',
        });
        rootDispatch({
          type: rootActionTypes.WORKSPACE_ERROR,
          payload: true,
        });
        return;
      }
    }

    if (isWorkspaceFlowEnabled) {
      if (didUserInitiallyHaveCXLicense) {
        const canSaveWorkspaces = updatedUserLevelCXWorkspaces.length !== 0;
        const originalUserLevelWorkspaceIds = originalUserLevelCXWorkspaces.map(
          (workspace) => workspace?.id,
        );
        const udpatedUserLevelWorkspaceIds = updatedUserLevelCXWorkspaces.map(
          (workspace) => workspace?.id,
        );
        const newChangesDetected =
          originalUserLevelWorkspaceIds?.sort().join(',') !==
          udpatedUserLevelWorkspaceIds?.sort().join(',');

        if (newChangesDetected && !canSaveWorkspaces) {
          setSnackbar({
            message: 'Please assign a workspace to this user to continue.',
            severity: 'error',
          });
          rootDispatch({
            type: rootActionTypes.WORKSPACE_ERROR,
            payload: true,
          });
          return;
        }
      } else {
        const canSaveWorkspaces = newCXUserWorkspaces.length !== 0;
        if (!canSaveWorkspaces) {
          setSnackbar({
            message: 'Please assign a workspace to this user to continue.',
            severity: 'error',
          });
          rootDispatch({
            type: rootActionTypes.WORKSPACE_ERROR,
            payload: true,
          });
          return;
        }
      }
    }

    const mappedTeamUser = [];
    setSending(true);

    // TEAMS UPDATE
    // Check if the admin wants to edit the user's team
    if (
      primaryTeam &&
      user.team?.filter((t) => t.id === primaryTeam).length === 0
    ) {
      // Optimistically update UI with new primary team
      userInput.teamDisplay = `${
        // If primaryTeam is not found in teamsOptions an app crashing error
        // is thrown, This error is most likely due to bad data on dev...
        teamsOptions.find((team) => team.key === primaryTeam).label
      }, ${assignedTeams?.map((team) => team.name).join(', ')}`;
    } else {
      // Optimistically update any teams changes besides primary team
      userInput.teamDisplay = assignedTeams
        ?.map((team) => team.name)
        .join(', ');
    }

    const assignedTeamIds = assignedTeams?.map((t) => t.id).sort();
    const baseTeamIds = baseAssignedTeams?.map((t) => t.id).sort();

    // Determine which teams to assign and remove
    if (!isEqual(assignedTeamIds, baseTeamIds)) {
      const teamsToAssign = differenceWith(assignedTeamIds, baseTeamIds);
      const teamsToUnassign = differenceWith(baseTeamIds, assignedTeamIds);

      if (teamsToAssign.length > 0) {
        teamsToAssign.forEach((teamId) => {
          mappedTeamUser.push({
            op: 'add',
            path: `/${teamId}/members/-`,
            value: user.nextiva_uuid ?? user.nextivaUuid,
          });
        });
      }
      if (teamsToUnassign.length > 0) {
        teamsToUnassign.forEach((teamId) => {
          mappedTeamUser.push({
            op: 'remove',
            path: `/${teamId}/members/${user.nextiva_uuid ?? user.nextivaUuid}`,
          });
        });
      }
    }

    // ROLES UPDATE
    // Check if the admin wants to edit the user's role
    if (role === nonSpecified.value) {
      saveRequestBody.drop_ids.push(...usersRoles.map((r) => r.id));
    } else {
      if (!usersRoles.some((r) => r.id === role)) {
        saveRequestBody.add_ids.push(role);
      }
      saveRequestBody.drop_ids.push(
        ...usersRoles.filter((r) => r.id !== role).map((r) => r.id),
      );
    }

    // Find the new role name from the options
    const roleOption = rolesOptions.find((ele) => ele.key === role);

    // Set the role display for optimistic update
    userInput.roleDisplay = '';
    userInput.roles = [];

    // Check that the role option was found
    if (roleOption) {
      userInput.roleDisplay = roleOption.label;
      userInput.roles = [
        {
          id: roleOption.key,
          name: roleOption.label,
          description: roleOption.description,
        },
      ];
    }

    // Check location is set and it does not equal the previous location
    if (
      location &&
      user.profiles.length > 0 &&
      location !== user.profiles[0].locationId
    ) {
      saveRequestBody.locationId = parseInt(location, 10);
      userInput.locationDisplay = getLocationName(
        locationsOptions,
        parseInt(location, 10),
      );
    }

    // Define the promises to create for updating the user
    const promises = [
      updateUser(
        user.email,
        authV3Enabled
          ? {
              ...userInput,
              profiles: user.profiles.map((profile, index) =>
                index === 0 ? { ...profile, locationId: location } : profile,
              ),
              phoneNumber: userInput.phone.number,
              team: assignedTeams,
              phone: undefined,
              checked: undefined,
              statusDisplay: undefined,
            }
          : saveRequestBody,
        user.nextivaUuid,
        authV3Enabled,
      ),
    ];

    // Check if the user has a CRM license by checking the CRM ID, and if the admin wants to update that user's primary team
    if (crmUserId && primaryTeam) {
      // Translate the SSO Role ID to CRM Department ID
      promises.push(
        getCrmDepartment(primaryTeam).then((payload) => {
          // Make certain the API returned a CRM department
          if (payload.data.length > 0) {
            const crmDepartmentId = payload.data[0].id;

            // Call the CRM API endpoints to update the CRM agent information
            updateUserDeps(crmUserId, crmDepartmentId)
              .then(() => {
                // There is a race condition where the department must be added
                // to the user's deps list before it can be assigned as default
                patchUserPrimaryTeam(crmUserId, crmDepartmentId).catch(() => {
                  setSnackbar({
                    message: 'Unable to update primary team.',
                    severity: 'error',
                  });
                });
              })
              .catch(() => {
                setSnackbar({
                  message: 'Unable to add primary team.',
                  severity: 'error',
                });
              });
          } else {
            setSnackbar({
              message: 'Unable to find primary team.',
              severity: 'error',
            });
          }
        }),
      );
    }

    // Get a comparable list of license IDs
    const assignedIds = [
      ...assignedLicenses.map((l) => l.id),
      ...licensesToAdd.map((l) => l.id),
    ].sort();
    const baseIds = baseAssignedLicenses.map((l) => l.id).sort();
    // If licenses have changed prepare promise
    if (!isEqual(assignedIds, baseIds)) {
      // Find added licenses and prepare them for API
      const licensesToAssign = differenceWith(assignedIds, baseIds);
      const allLicenses = [...assignedLicenses, ...licensesToAdd];

      if (licensesToAssign.length > 0) {
        const assigned = [];
        const assignBody = {
          licenses: allLicenses.reduce((newLicenses, license) => {
            if (licensesToAssign.includes(license.id)) {
              assigned.push(license);
              newLicenses.push({
                license_id: license.id,
                expiration_date: license.expiration_date,
              });
            }

            return newLicenses;
          }, []),
          profile_id: user.profiles[0].id,
        };
        const featureList = assignedLicensesFeatureList(allLicenses);

        if (featureList.some((l) => l === 'PLATFORM_VOICE_ACCESS')) {
          promises.push(Promise.resolve('ASSIGNED_PLATFORM_VOICE_ACCESS'));
        }
        promises.push(Promise.resolve({ licensesToAssign }));
        promises.push(
          assignUserLicenses(
            authV3Enabled
              ? assignBody.licenses.map((license) => license.license_id)
              : assignBody,
            authV3Enabled,
            corpAcctNbr,
            user.nextivaUuid,
          ),
        );
      }

      // Find deleted licenses and prepare them for API
      const licensesToUnassign = differenceWith(baseIds, assignedIds);
      // Is "PLATFORM_VOICE_ACCESS" in unassigned licenses
      if (licensesToUnassign.length > 0) {
        const unassigned = [];
        let unassignBody;
        if (authV3Enabled) {
          unassignBody = baseAssignedLicenses.reduce((newLicenses, license) => {
            if (licensesToUnassign.includes(license.id)) {
              unassigned.push(license);
              newLicenses.push(license.id);
            }
            return newLicenses;
          }, []);
        } else {
          unassignBody = {
            licenses: baseAssignedLicenses.reduce((newLicenses, license) => {
              if (licensesToUnassign.includes(license.id)) {
                unassigned.push(license);
                newLicenses.push({
                  license_id: license.id,
                  expiration_date: license.expiration_date,
                });
              }
              return newLicenses;
            }, []),
            profile_id: user.profiles[0].id,
          };
        }
        const featureList = assignedLicensesFeatureList(unassigned);
        if (featureList.some((l) => l === 'PLATFORM_VOICE_ACCESS')) {
          promises.push(Promise.resolve('UNASSIGNED_PLATFORM_VOICE_ACCESS'));
        }

        promises.push({ licensesToUnassign });
        promises.push(
          unassignUserLicenses(
            unassignBody,
            user.nextivaUuid,
            authV3Enabled,
            corpAcctNbr,
          ),
        );
      }

      // check if user will have a license after update
      const removeAllLicenses = isEqual(baseIds, licensesToUnassign);

      switch (true) {
        case baseIds.length === 0 && licensesToAssign.length === 0:
          promises.push(Promise.resolve('USER_DOES_NOT_HAVE_LICENSE'));
          break;
        case baseIds.length > 0 &&
          licensesToUnassign.length > 0 &&
          removeAllLicenses: {
          promises.push(Promise.resolve('USER_DOES_NOT_HAVE_LICENSE'));
          break;
        }
        default:
          promises.push(Promise.resolve('USER_HAS_LICENSE'));
      }
    }

    // Run all the backend API calls to update the user
    Promise.all(promises)
      .then((data) => {
        let userObj = { ...userInput, updatedDate: new Date() };
        const toAssign = data.find((d) => d?.licensesToAssign?.length > 0);
        const toUnassign = data.find((d) => d?.licensesToUnassign?.length > 0);

        const hasNextOsIdent =
          !!user?.profiles[0]?.nextOS?.primary_identifier ||
          !!user?.profiles[0]?.nextOS?.primaryIdentifier;
        const hasNoLicense = data.some(
          (d) => d === 'USER_DOES_NOT_HAVE_LICENSE',
        );
        const hasLicense = data.some((d) => d === 'USER_HAS_LICENSE');
        const unassignVoice = data.some(
          (d) => d === 'UNASSIGNED_PLATFORM_VOICE_ACCESS',
        );
        const assignVoice = data.some(
          (d) => d === 'ASSIGNED_PLATFORM_VOICE_ACCESS',
        );

        if (isWorkspaceFlowEnabled) {
          if (didUserInitiallyHaveCXLicense) {
            const udpatedUserLevelWorkspaceIds = updatedUserLevelCXWorkspaces.map(
              (workspace) => workspace?.id,
            );
            const cxUserPayload = {
              email: user?.email,
              firstName: user?.first_name ?? user?.firstName,
              lastName: user?.last_name ?? user?.lastName,
              nextivaUuid: user?.nextivaUuid ?? user?.nextiva_uuid,
              payloadProfiles: udpatedUserLevelWorkspaceIds,
            };
            updateCXUser.mutate(cxUserPayload);
          } else {
            createCXUser.mutate({
              userUuid: user?.nextivaUuid ?? user?.nextiva_uuid,
              role: userInput?.roleDisplay,
            });
          }
        }

        const noNextOsIdentNoLicense = [
          {
            ...userInput.profiles[0],
            nextOS: {
              portal: '',
              primary_identifier: '',
              secondary_identifier: '',
            },
            productFeatures: [],
          },
        ];
        const noNextOsIdentHasLicense = [
          {
            ...userInput.profiles[0],
            nextOS: {
              portal: '',
              primary_identifier: '',
              secondary_identifier: '',
            },
            productFeatures: [
              {
                features: ['OPTIMISTICALLY_UPDATED'],
                product: 'OPTIMISTICALLY_UPDATED',
              },
            ],
          },
        ];
        const nextOsIdentHasLicense = [
          {
            ...userInput.profiles[0],
            nextOS: {
              portal: '',
              primary_identifier: 'optimistically-updated',
              secondary_identifier: '',
            },
            productFeatures: [
              {
                features: ['OPTIMISTICALLY_UPDATED'],
                product: 'OPTIMISTICALLY_UPDATED',
              },
            ],
          },
        ];
        const nextOsIdent = [
          {
            ...userInput.profiles[0],
            nextOS: {
              portal: '',
              primary_identifier: '',
              secondary_identifier: '',
            },
            productFeatures: [
              {
                features: ['PLATFORM_VOICE_ACCESS'],
                product: 'VOICE_GLOBAL',
              },
            ],
          },
        ];
        const nextOsIdentHasNoLicense = [
          {
            ...userInput.profiles[0],
            nextOS: {
              portal: '',
              primary_identifier: 'optimistically-updated',
              secondary_identifier: '',
            },
            productFeatures: [],
          },
        ];

        switch (true) {
          case assignVoice:
            userObj = { ...userObj, profiles: nextOsIdent };
            break;
          case unassignVoice && hasLicense:
          case !hasNextOsIdent && hasLicense:
            userObj = { ...userObj, profiles: noNextOsIdentHasLicense };
            break;
          case hasNextOsIdent && hasLicense:
            userObj = { ...userObj, profiles: nextOsIdentHasLicense };
            break;
          case unassignVoice && hasNoLicense:
          case !hasNextOsIdent && hasNoLicense:
            userObj = { ...userObj, profiles: noNextOsIdentNoLicense };
            break;
          case hasNextOsIdent && hasNoLicense:
            userObj = { ...userObj, profiles: nextOsIdentHasNoLicense };
            break;
          default:
          // do nothing
        }

        dispatch({
          type: actionTypes.OPTIMISTICALLY_UPDATE_USER,
          user: userObj,
        });
        dispatch({
          type: actionTypes.OPTIMISTICALLY_UPDATE_LICENSES,
          assigned: toAssign?.licensesToAssign,
          unassigned: toUnassign?.licensesToUnassign,
        });

        if (
          isAccountManager(user, companyInfo.data || {}) &&
          !!saveRequestBody.email
        ) {
          setSnackbar({
            message: `${user.first_name ?? user.firstName} ${
              user.last_name ?? user.lastName
            } has been updated, billing notifications will now be sent to ${
              saveRequestBody.email
            }.!`,
            severity: 'info',
          });
          setSending(false);
          setTimeout(() => {
            toggleDrawer();
            if (unsavedChangesFlow)
              window.location = `${window.location.origin}/${LICENSES_LINK}`;
          }, 2000);
          return;
        }

        setSnackbar({
          message: `${user.first_name ?? user.firstName} ${
            user.last_name ?? user.lastName
          } has been updated!`,
          severity: 'success',
        });
        setSending(false);
        updateRootUserList();
        if (!isEqual(assignedIds, baseIds))
          queryClient.invalidateQueries(['licenseList']);
        if (
          location &&
          user.profiles.length > 0 &&
          location !== user.profiles[0].locationId
        ) {
          queryClient.invalidateQueries(['locations']);
        }
        if (!isEqual(assignedTeamIds, baseTeamIds)) {
          queryClient.invalidateQueries(['getTeamsOptions']);
          queryClient.invalidateQueries(['getTeams']);
        }
        queryClient.invalidateQueries(['getUsersBasicInfo']);
        if (roleOption) {
          queryClient.invalidateQueries(['getRoles']);
          queryClient.invalidateQueries(['getPermissionsMacros']);
          queryClient.invalidateQueries(['getServiceResources']);
          queryClient.invalidateQueries(['getManagersInfo']);
        }
        setTimeout(() => {
          toggleDrawer();
          if (unsavedChangesFlow)
            window.location = `${window.location.origin}/${LICENSES_LINK}`;
        }, 2000);
      })
      .catch((err) => {
        setPristine(true);
        setSending(false);
        if (Object.values(err?.errors)[0] === 'Failed to assign license.') {
          setSnackbar({
            message: `You don't have any licenses left to assign. Call 1.800.799.0600`,
            severity: 'error',
          });
        } else {
          setSnackbar({
            message: 'Unable to update user. Please try again later.',
            severity: 'error',
          });
        }
      });
    // in the future IAMOS will talk to TOS to update teams so remove once fully switched to IAMOS
    if (mappedTeamUser.length && authV3Enabled === false)
      updateUserTeamMapping.mutate({ corpAcctNbr, body: mappedTeamUser });
  };

  const onCancelClick = () => {
    toggleDrawer();
    setValidation('');
  };

  const teamOptionsMenuItems = teamsOptions.map(({ key, label }) => ({
    value: key,
    key,
    label,
  }));

  const active = authV3Enabled
    ? user.profiles.find(
        (profile) => profile.corporateAccountNumber === corpAcctNbr,
      )?.active
    : activeStatus;

  const showPrimaryTeam =
    licenses
      .find((lic) => lic.product === 'CRM')
      ?.features.some((permission) => CRM_LICENSES.includes(permission)) &&
    crmUserId;

  const avatarInitials = authV3Enabled
    ? (userInput?.firstName?.[0] || '') + (userInput?.lastName?.[0] || '')
    : (userInput?.first_name[0] || '') + (userInput?.last_name[0] || '');

  const saveUnsavedChanges = (unsavedChangesFlow) => {
    if (
      !validation?.first_name &&
      !validation?.last_name &&
      !validation?.email &&
      !validation?.phone
    )
      onSave(null, unsavedChangesFlow);
  };

  return (
    <div ref={editUserDom} className="m-edit-user">
      <div className={classes.editUser__container}>
        <Grid container spacing={0}>
          <Grid item xs={12}>
            <Grid
              container
              spacing={0}
              justifyContent="center"
              alignItems="center"
              direction="column"
            >
              {active || (
                <Alert severity="info">
                  This user is not active, to make changes reactivate user from
                  Current Users page.
                </Alert>
              )}
              <Avatar size={120}>{avatarInitials}</Avatar>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Grid
              container
              spacing={0}
              justifyContent="center"
              alignItems="center"
            >
              <div className={classes.editUser__reset}>
                <Button
                  style={ResetButtonStyle}
                  variant="outlined"
                  color="primary"
                  onClick={resetUserPassword}
                  disabled={!active}
                  data-testid="PlatformAdminUI-UsersLink-EditUser-ResetPassword"
                >
                  Reset password
                </Button>
              </div>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <div className={classes.editUser__lineBreak}>
              <Divider variant="middle" />
            </div>
          </Grid>
          <Grid item xs={12}>
            <div
              className={clsx('Heading3', classes.editUser__header)}
              data-testid="PlatformAdminUI-UsersLink-EditUser-UserInformation"
            >
              User Information
            </div>
          </Grid>
          <Grid item xs={12}>
            <TooltipWithEnable
              enableTooltip={multipleProfiles}
              text="This user must update their own profile information. If this user is unable to log in to update, please contact support."
            >
              <div className={classes.editUser__inputField}>
                <TextField
                  id="first_name"
                  fullWidth
                  error={!!validation.first_name}
                  helperText={
                    !!validation.first_name &&
                    `First name ${validation.first_name}`
                  }
                  label="First Name"
                  value={userInput.first_name ?? userInput.firstName}
                  onChange={(e) =>
                    handleInputChange(
                      e,
                      authV3Enabled ? 'firstName' : 'first_name',
                    )
                  }
                  disabled={multipleProfiles || !active}
                  inputProps={{
                    'data-testid':
                      'PlatformAdminUI-UsersLink-EditUser-FirstName',
                  }}
                  data-testid="PlatformAdminUI-EditUser-TextField-FirstName"
                />
              </div>
            </TooltipWithEnable>
          </Grid>
          <Grid item xs={12}>
            <TooltipWithEnable
              enableTooltip={multipleProfiles}
              text="This user must update their own profile information. If this user is unable to log in to update, please contact support."
            >
              <div className={classes.editUser__inputField}>
                <TextField
                  id="last_name"
                  fullWidth
                  error={!!validation.last_name}
                  helperText={
                    !!validation.last_name &&
                    `Last name ${validation.last_name}`
                  }
                  label="Last Name"
                  value={userInput.last_name ?? userInput.lastName}
                  onChange={(e) =>
                    handleInputChange(
                      e,
                      authV3Enabled ? 'lastName' : 'last_name',
                    )
                  }
                  disabled={multipleProfiles || !active}
                  inputProps={{
                    'data-testid':
                      'PlatformAdminUI-UsersLink-EditUser-LastName',
                  }}
                  data-testid="PlatformAdminUI-EditUser-TextField-LastName"
                />
              </div>
            </TooltipWithEnable>
          </Grid>
          <Grid item xs={12}>
            <TooltipWithEnable
              enableTooltip={multipleProfiles}
              text="This user must update their own profile information. If this user is unable to log in to update, please contact support."
            >
              <div className={classes.editUser__inputField}>
                <TextField
                  fullWidth
                  id="email"
                  error={!!validation.email}
                  helperText={!!validation.email && validation.email}
                  label={<Hint tool="Email (User Id)" tip={EMAIL_ADDRESS} />}
                  value={userInput.email || ''}
                  onChange={(e) => handleInputChange(e, 'email')}
                  disabled={multipleProfiles || !active}
                  inputProps={{
                    'data-testid': 'PlatformAdminUI-UsersLink-EditUser-Email',
                  }}
                  data-testid="PlatformAdminUI-EditUser-TextField-Email"
                />
              </div>
            </TooltipWithEnable>
          </Grid>
          {locationBasedAdminFeature && (
            <>
              <Grid item xs={12}>
                <div className={classes.editUser__inputField}>
                  <InputLabel id="AdminAccess-Option">
                    <Hint
                      tool="Administration access"
                      tip={ADMINISTRATION_ACCESS}
                    />
                  </InputLabel>
                  <Select
                    id="adminAccess"
                    labelId="AdminAccess-Option"
                    onChange={(e) => handleInputChange(e, 'adminAccess')}
                    value={adminAccess}
                    fullWidth
                    disabled={!active}
                    data-testid="PlatformAdminUI-EditUser-SelectAdminAccess"
                  >
                    {adminAccessOptions.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </div>
              </Grid>
              {adminAccess === 'certain_loc_admin' && (
                <Grid item xs={12}>
                  <div className={classes.editUser__inputField}>
                    <InputLabel id="AdminLocations-Options">
                      <Hint tool="Admin location(s)" tip={ADMIN_LOCATIONS} />
                    </InputLabel>
                    <Select
                      labelId="AdminLocations-Options"
                      id="adminLocations"
                      multiple
                      onChange={(e) => handleInputChange(e, 'adminLocations')}
                      value={adminLocations}
                      fullWidth
                      disabled={!active}
                      data-testid="PlatformAdminUI-EditUser-SelectAdminLocation"
                    >
                      {locationsOptions.map((option) => (
                        <MenuItem key={option.key} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </div>
                </Grid>
              )}
            </>
          )}
          <Grid item xs={12}>
            <div className={classes.editUser__inputField}>
              <InputLabel id="Roles-Option">
                <Hint tool={rolesTool} tip={rolesTip} />
              </InputLabel>
              <Select
                fullWidth
                labelId="Roles-Option"
                id="roles"
                disabled={adminAccess === 'entire_acc_admin' || !active}
                value={role}
                onChange={(e) => handleInputChange(e, 'role')}
                data-testid="PlatformAdminUI-EditUser-SelectRoles"
              >
                {filteredRolesOptions.map((roleOption) => (
                  <MenuItem key={roleOption.key} value={roleOption.value}>
                    {roleOption.label}
                  </MenuItem>
                ))}
              </Select>
            </div>
          </Grid>
          {isWorkspaceFlowEnabled && (
            <WorkspaceChipSelect
              flowType="EDIT_USER"
              userUuid={user.nextivaUuid ?? user.nextiva_uuid}
              didUserInitiallyHaveCXLicense={didUserInitiallyHaveCXLicense}
            />
          )}
          {showPrimaryTeam && (
            <Grid item xs={12}>
              <div className={classes.editUser__inputField}>
                <InputLabel id="Teams-Option">
                  <Hint tool="Primary Team" tip={PRIMARY_TEAM} />
                </InputLabel>
                <TooltipWithEnable
                  enableTooltip={primaryTeamDisable}
                  text="To configure Primary Team this user must have a CRM license assigned."
                >
                  <Select
                    fullWidth
                    labelId="Teams-Option"
                    id="teams"
                    value={primaryTeam}
                    disabled={primaryTeamDisable || !active}
                    onChange={(e) => handleInputChange(e, 'primaryTeam')}
                    data-testid="PlatformAdminUI-UsersLink-EditUser-PrimaryPhoneNumber"
                  >
                    {teamOptionsMenuItems.map((option) => (
                      <MenuItem key={option.key} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </TooltipWithEnable>
              </div>
            </Grid>
          )}
          {(multipleProfiles && !initialPhoneVal.number) === false && (
            <Grid item xs={12}>
              <TooltipWithEnable
                enableTooltip={multipleProfiles || phoneNumberDisable}
                text={
                  multipleProfiles
                    ? 'This user must update their own profile information. If this user is unable to log in to update, please contact support.'
                    : 'Phone numbers cannot be edited for users who already have voice setup'
                }
              >
                <div className={classes.editUser__inputField}>
                  <MuiThemeProvider theme={defaultTheme}>
                    <PhoneField
                      phoneNumber={initialPhoneVal.number || ''}
                      placeholder=" "
                      code={initialPhoneVal.code || ''}
                      fullWidth
                      id="phone"
                      error={!!validation.phone}
                      errorMessage={validation.phone}
                      label="Primary Phone Number"
                      onChange={handlePhoneField}
                      disabled={
                        (multipleProfiles && initialPhoneVal.number) ||
                        phoneNumberDisable ||
                        !active
                      }
                      data-testid="PlatformAdminUI-UsersLink-EditUser-PrimaryPhoneNumber"
                    />
                  </MuiThemeProvider>
                </div>
              </TooltipWithEnable>
            </Grid>
          )}
          <Grid item xs={12}>
            <div className={classes.editUser__inputField}>
              <InputLabel id="Locations-Option">LOCATION (optional)</InputLabel>
              <TooltipWithEnable
                enableTooltip={locationDisable}
                text="Locations cannot be edited for users who already have voice setup"
              >
                <Select
                  fullWidth
                  value={location || ''}
                  disabled={locationDisable || !active}
                  id="location"
                  labelId="Locations-Option"
                  className={classes.root}
                  onChange={(e) => handleInputChange(e, 'location')}
                  errorMessage="Please select a location."
                  data-testid="PlatformAdminUI-EditUser-SelectLocation"
                >
                  {locationsOptions.map((opt) => (
                    <MenuItem key={opt.key} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </TooltipWithEnable>
            </div>
          </Grid>
          <Grid item xs={12}>
            <div className={classes.editUser__fieldPadding}>
              <InputLabel id="TimeZone-Option">TIME ZONE (optional)</InputLabel>
              <TooltipWithEnable
                enableTooltip={multipleProfiles}
                text="This user must update their own profile information. If this user is unable to log in to update, please contact support."
              >
                <Select
                  fullWidth
                  id="timeZone"
                  labelId="TimeZone-Option"
                  className={classes.root}
                  value={userInput.timeZone || ''}
                  onChange={(e) => handleInputChange(e, 'timeZone')}
                  disabled={multipleProfiles || !active}
                  data-testid="PlatformAdminUI-EditUser-SelectTimeZone"
                >
                  {timezoneOptions.data?.map((option) => (
                    <MenuItem key={option.key} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </TooltipWithEnable>
            </div>
          </Grid>
          {impersonation && (userInput.security_pin || userInput?.securityPin) && (
            <Grid item xs={12}>
              <div className={classes.editUser__inputField}>
                <TextField
                  id="securityPin"
                  fullWidth
                  error={!!validation.securityPin}
                  helperText={
                    !!validation.securityPin &&
                    `First name ${validation.securityPin}`
                  }
                  label="Security Pin"
                  value={userInput.security_pin || userInput?.securityPin}
                  disabled
                />
              </div>
            </Grid>
          )}
        </Grid>
        <Grid item xs={12}>
          <Section id="teamssection" title="Teams">
            <TeamsSection
              primaryTeam={primaryTeam}
              showPrimaryTeam={showPrimaryTeam}
            />
          </Section>
        </Grid>
        <Grid item xs={12}>
          <Section id="licenses" title="Licenses">
            <LicenseSection
              unsavedUserChanges={unsavedEditUserChanges}
              saveChanges={saveUnsavedChanges}
            />
          </Section>
        </Grid>
        <Grid item xs={12}>
          <Section id="history" title="Update History">
            <HistorySection user={user} />
          </Section>
        </Grid>
      </div>
      <div className="m-drawer-footer">
        <div className="m-drawer-footer__container">
          <div className="m-drawer-footer__container__action-bar">
            <div className="m-drawer-footer__container__action-bar__button">
              <Button
                id="cancel-drawer"
                className="footer__btn"
                color="secondary"
                variant="contained"
                onClick={onCancelClick}
                fullWidth
                data-testid="PlatformAdminUI-UsersLink-EditUser-CancelButton"
              >
                Cancel
              </Button>
            </div>
            <div className="m-drawer-footer__container__action-bar__button">
              <Button
                id="submit-form"
                type="button"
                fullWidth
                color="primary"
                variant="contained"
                onClick={onSave}
                className="footer__btn"
                disabled={pristine || disabled || !role || !active || isOpen}
                data-testid="PlatformAdminUI-UsersLink-EditUser-SaveButton"
              >
                Save
              </Button>
            </div>
            {sending && (
              <div className="m-drawer-footer__container__action-bar__spinner">
                <CircularProgress
                  style={{ color: '#009FE0' }}
                  variant="indeterminate"
                  size={36}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

EditUser.propTypes = {
  impersonation: PropTypes.bool.isRequired,
  toggleDrawer: PropTypes.func.isRequired,
};

export default EditUser;
